import { Component, DestroyRef, Inject, type OnInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import {
  combineLatest,
  defer,
  firstValueFrom,
  of,
  type Observable,
  shareReplay,
  startWith,
} from 'rxjs';
import {
  appendTimeEntry,
  buildTimeEntryStartTime,
  type CustomerSummary,
  type FirestoreOrgaUser,
  type FirestoreProject,
  type FirestoreWork,
  type IsoDate,
  mapOrgaUserSummary,
  type OrgaUserSummary,
  type PositionSummary,
  type PositionType2,
  type ProjectSummary,
  timeEntriesOverlap,
  timerTimeEntries,
  type TimeEntry,
  type WorkEntity2,
  type WorkPositionSummary,
} from '@jessie/commons';
import { SlideOverComponent } from '../../../components/slide-overs/slide-over/slide-over.component';
import { SlideOverHeaderComponent } from '../../../components/slide-overs/slide-over/slide-over-header/slide-over-header.component';
import { SlideOverContentComponent } from '../../../components/slide-overs/slide-over/slide-over-content/slide-over-content.component';

import { SlideOverFooterComponent } from '../../../components/slide-overs/slide-over/slide-over-footer/slide-over-footer.component';
import { DropdownMinimalMenuComponent } from '../../../components/dropdown-minimal-menu/dropdown-minimal-menu.component';
import { DropdownButtonComponent } from '../../../components/dropdown-minimal-menu/dropdown-button/dropdown-button.component';
import { NgIconWrapperComponent } from '../../../components/icons/ng-icon-wrapper/ng-icon-wrapper.component';
import { DropdownItemComponent } from '../../../components/dropdown-minimal-menu/dropdown-item/dropdown-item.component';
import { TailwindButtonDirective } from '../../../components/button/tailwind-button.directive';
import { WorkSuggestionsComponent } from '../../home/components/work-suggestions/work-suggestions.component';
import { WorkService } from '../../../services/work.service';
import { TimerService } from '../../../services/timer.service';
import { SessionStateService } from '../../../services/session-state.service';
import { DateInputComponent } from '../../../components/input/date-input/date-input.component';
import { TextInputComponent } from '../../../components/input/text-input/text-input.component';
import { WorkTimeInputComponent } from '../../../components/input/work-time-input/work-time-input.component';
import { DropdownComponent } from '../../../components/dropdown/dropdown.component';
import { OrgaUserService } from '../../../services/orga-user.service';
import { filter, map, switchMap } from 'rxjs/operators';
import { ProjectService } from '../../../services/project.service';
import { addMinutes, format, formatISO, isValid, parseISO } from 'date-fns';
import { SlideOverAlertComponent } from '../../../components/slide-overs/slide-over/slide-over-alert/slide-over-alert.component';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormInputWrapperComponent } from '../../../components/input/form-input-wrapper/form-input-wrapper.component';
import { TailwindInputDirective2 } from '../../../components/input/tailwind-input2.directive';

const workSlideOverDiTokens = [
  WorkService,
  TimerService,
  SessionStateService,
  OrgaUserService,
  ProjectService,
];
void workSlideOverDiTokens;

export interface WorkForm {
  project: FormControl<ProjectSummary | null>;
  customer: FormControl<CustomerSummary | null>;
  position: FormControl<(PositionSummary & { billable: boolean; type: PositionType2 }) | null>;
  orgaUser: FormControl<OrgaUserSummary>;
  date: FormControl<IsoDate | null>;
  workMinutes: FormControl<number | null>;
  description: FormControl<string | null>;
}

export interface WorkSlideOverInput {
  work?: FirestoreWork;
  date?: IsoDate;
  customer?: CustomerSummary;
  project?: ProjectSummary;
  position?: WorkPositionSummary;
  description?: string;
  workMinutes?: number;
  startTime?: string;
  sessionEntryId?: string;
}

@Component({
  selector: 'app-work-slide-over',
  templateUrl: './work-slide-over.component.html',
  imports: [
    SlideOverComponent,
    SlideOverHeaderComponent,
    SlideOverContentComponent,
    ReactiveFormsModule,
    SlideOverFooterComponent,
    DropdownMinimalMenuComponent,
    DropdownButtonComponent,
    NgIconWrapperComponent,
    DropdownItemComponent,
    TailwindButtonDirective,
    DateInputComponent,
    TextInputComponent,
    WorkTimeInputComponent,
    FormInputWrapperComponent,
    TailwindInputDirective2,
    DropdownComponent,
    SlideOverAlertComponent,
    WorkSuggestionsComponent,
    AsyncPipe,
  ],
})
export class WorkSlideOverComponent implements OnInit {
  title!: string;

  workForm!: FormGroup<WorkForm>;

  saving = false;
  readonly sessionStartTime = new FormControl('', { nonNullable: true });
  readonly sessionEndTime = new FormControl('', { nonNullable: true });
  timeEntryError: string | null = null;
  suggestions: Observable<FirestoreWork[]> = of([]);
  showSuggestions = false;
  private skipPositionReset = false;

  loggedInUser = this.sessionState.getOrgaUser().pipe(
    filter((user): user is FirestoreOrgaUser => !!user),
    map((user) => mapOrgaUserSummary(user))
  );
  orgaUsers = this.orgaUserService.getAllOrgaUserSummaries();

  projects = this.projectService.getActiveProjects().pipe(shareReplay(1));

  projectSummaries = combineLatest([this.projects, this.sessionState.getUser()]).pipe(
    map(([projects, user]) =>
      projects
        .map((project) => ({
          id: project.id,
          displayName: project.data.name || 'No Displayname',
          _isFavorite: !!(user && project.data.favorite?.[user.id]),
        }))
        .sort((a, b) =>
          a._isFavorite === b._isFavorite
            ? a.displayName.localeCompare(b.displayName)
            : a._isFavorite
              ? -1
              : 1
        )
        .map(({ id, displayName }) => ({ id, displayName }))
    )
  );

  positionSummaries: Observable<WorkPositionSummary[]> = defer(() =>
    combineLatest([
      this.workForm.controls.project.valueChanges.pipe(
        startWith(this.workForm.controls.project.value),
        switchMap((projectSummary) =>
          this.projects.pipe(
            map((projects) => projects.find((project) => project.id === projectSummary?.id) ?? null)
          )
        ),
        filter((project): project is FirestoreProject => !!project)
      ),
      this.sessionState.getUser(),
    ]).pipe(
      map(([project, user]) =>
        this.projectService
          .getWorkPositionSummaries(project)
          .map((pos) => ({
            ...pos,
            _isFavorite: !!(user && project.data.positions?.[pos.id]?.favorite?.[user.id]),
          }))
          .sort((a, b) =>
            a._isFavorite === b._isFavorite
              ? a.displayName.localeCompare(b.displayName)
              : a._isFavorite
                ? -1
                : 1
          )
          .map(({ _isFavorite, ...rest }) => rest)
      )
    )
  );

  constructor(
    private workService: WorkService,
    private timerService: TimerService,
    private sessionState: SessionStateService,
    private orgaUserService: OrgaUserService,
    private projectService: ProjectService,
    @Inject(DIALOG_DATA) public data: WorkSlideOverInput | null,
    @Inject(DialogRef) public dialogRef: DialogRef,
    @Inject(DestroyRef) private destroyRef: DestroyRef
  ) { }

  async ngOnInit() {
    const loggedInUser = await firstValueFrom(this.loggedInUser);
    let work: WorkEntity2 | null = null;
    if (this.data?.work) {
      work = this.data.work.data;
    }
    await this.initForm2(loggedInUser, work);
    this.title = this.data?.work ? 'Stunden bearbeiten' : 'Stunden erfassen';

    if (!this.data?.work) {
      this.suggestions = this.sessionState.getOrgaUserId().pipe(
        switchMap((orgaUserId) =>
          this.workService.getWorkSuggestions(orgaUserId, 20, 5, this.projects)
        ),
        shareReplay(1)
      );
      this.showSuggestions = true;
    }
  }

  async initForm2(loggedInUser: OrgaUserSummary, work: WorkEntity2 | null) {
    this.workForm = new FormGroup<WorkForm>({
      orgaUser: new FormControl(work?.orgaUser ?? loggedInUser, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      project: new FormControl(work?.project ?? this.data?.project ?? null, [Validators.required]),
      position: new FormControl(work?.position ?? this.data?.position ?? null, [
        Validators.required,
      ]),
      date: new FormControl(
        work?.date ?? this.data?.date ?? formatISO(new Date(), { representation: 'date' }),
        [Validators.required]
      ),
      workMinutes: new FormControl(this.getInitialWorkMinutes(work), [Validators.required]),
      description: new FormControl(work?.description ?? this.data?.description ?? null, [
        Validators.maxLength(240),
      ]),
      customer: new FormControl(work?.customer ?? this.data?.customer ?? null),
    });
    this.initializeSessionTimeControls(work);
    this.disableEnableFields();
    this.workForm.controls.project.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (project) => {
        this.workForm.controls.customer.patchValue(
          project ? await this.getCustomer(project.id) : null
        );
        if (!this.skipPositionReset) {
          this.workForm.controls.position.patchValue(null);
        }
        this.skipPositionReset = false;
      });

    if (!work && this.data?.startTime) {
      this.workForm.markAsDirty();
    }
  }

  disableEnableFields() {
    if (this.data?.work && this.data?.work.data.invoice) {
      this.workForm.disable({ emitEvent: false });
      return;
    }

    this.workForm.enable({ emitEvent: false });
    if (this.data?.work && this.data.sessionEntryId) {
      this.workForm.controls.date.disable({ emitEvent: false });
      if (!this.isSessionSourceSameDay) {
        this.workForm.disable({ emitEvent: false });
      }
    }
  }

  async selectSuggestion(suggestion: FirestoreWork) {
    this.skipPositionReset = true;
    this.workForm.patchValue({
      project: suggestion.data.project,
      position: suggestion.data.position,
      description: suggestion.data.description,
      customer: await this.getCustomer(suggestion.data.project.id),
    });
    this.workForm.markAsDirty();
    this.showSuggestions = false;
  }

  async saveWork() {
    if (this.isSessionMode && !this.isSessionSourceSameDay) {
      return;
    }

    this.workForm.markAllAsTouched();
    const sessionChange = this.isSessionMode ? this.buildSessionTimeEntry() : null;
    if (this.isSessionMode) {
      this.timeEntryError = null;
      if (!sessionChange) {
        this.timeEntryError = 'Das Ende muss nach dem Start liegen.';
        return;
      }
    }
    if (!this.workForm.valid) {
      return;
    }
    const isDragCreatedWork = !this.data?.work && !!this.data?.startTime;
    if (!this.workForm.dirty && !isDragCreatedWork) {
      this.closeSlideOver();
      return;
    }
    this.saving = true;
    try {
      if (this.data?.work) {
        const savedWork = this.data.work;
        const sessionEntryId = this.data.sessionEntryId;
        if (sessionChange && sessionEntryId) {
          const formValue = {
            ...this.workForm.getRawValue(),
            date: savedWork.data.date,
          } as WorkEntity2;
          const now = new Date();
          try {
            const [works, timers] = await Promise.all([
              firstValueFrom(
                this.workService.getAllWorkForUser(
                  formValue.orgaUser.id,
                  sessionChange.sessionDate,
                  sessionChange.sessionDate
                )
              ),
              firstValueFrom(this.timerService.getTimersByDate(sessionChange.sessionDate)),
            ]);
            const candidateEntries = [
              ...works.flatMap((candidateWork) =>
                Object.entries(candidateWork.data.timeEntries ?? {})
                  .filter(
                    ([candidateEntryId]) =>
                      candidateWork.id !== savedWork.id || candidateEntryId !== sessionEntryId
                  )
                  .map(([, entry]) => entry)
              ),
              ...timers.flatMap((timer) => Object.values(timerTimeEntries(timer.data, now))),
            ];
            if (candidateEntries.some((entry) => timeEntriesOverlap(sessionChange.nextEntry, entry))) {
              this.timeEntryError = 'Der gewählte Zeitraum überschneidet sich mit einem bestehenden Eintrag.';
              return;
            }
            await this.workService.updateWorkSession(
              savedWork,
              sessionEntryId,
              formValue,
              sessionChange.nextEntry
            );
          } catch {
            this.timeEntryError = 'Die Zeitänderung konnte nicht gespeichert werden.';
            return;
          }
        } else {
          await this.workService.updateWork(
            savedWork,
            this.workForm.getRawValue() as WorkEntity2
          );
        }
      } else {
        const formValue = this.workForm.getRawValue();
        await this.workService.createWorks([
          {
            ...formValue,
            invoice: null,
            timeEntries: this.buildTimeEntries(formValue.workMinutes, formValue.date),
          } as WorkEntity2,
        ]);
      }
      this.closeSlideOver({ saved: true });
    } finally {
      this.saving = false;
    }
  }

  get isSessionMode(): boolean {
    return !!this.data?.work && !!this.data.sessionEntryId;
  }

  get isSessionSourceSameDay(): boolean {
    const sessionEntryId = this.data?.sessionEntryId;
    const sessionEntry = sessionEntryId
      ? this.data?.work?.data.timeEntries?.[sessionEntryId]
      : null;
    const start = sessionEntry ? parseISO(sessionEntry.startTime) : null;
    const end = start && sessionEntry ? addMinutes(start, sessionEntry.totalMinutes) : null;

    return (
      !!start &&
      !!end &&
      isValid(start) &&
      isValid(end) &&
      formatISO(start, { representation: 'date' }) === formatISO(end, { representation: 'date' })
    );
  }

  get sessionTimeControlsDisabled(): boolean {
    return !!this.data?.work?.data.invoice || this.saving || !this.isSessionSourceSameDay;
  }

  get sessionDuration(): string {
    const startMinutes = this.timeToMinutes(this.sessionStartTime.value);
    const endMinutes = this.timeToMinutes(this.sessionEndTime.value);
    if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) {
      return '–';
    }

    const totalMinutes = endMinutes - startMinutes;
    return `${Math.floor(totalMinutes / 60)} h ${totalMinutes % 60} min`;
  }

  updateSessionStartTime(value: string) {
    this.sessionStartTime.setValue(value);
    this.sessionStartTime.markAsDirty();
    this.synchroniseSessionDuration();
  }

  updateSessionEndTime(value: string) {
    this.sessionEndTime.setValue(value);
    this.sessionEndTime.markAsDirty();
    this.synchroniseSessionDuration();
  }

  private initializeSessionTimeControls(work: WorkEntity2 | null) {
    if (!this.data?.sessionEntryId) {
      return;
    }

    const sessionEntry = work?.timeEntries?.[this.data.sessionEntryId];
    const start = sessionEntry ? parseISO(sessionEntry.startTime) : null;
    if (!sessionEntry || !start || !isValid(start) || !Number.isFinite(sessionEntry.totalMinutes)) {
      return;
    }

    const end = addMinutes(start, sessionEntry.totalMinutes);
    if (!isValid(end)) {
      return;
    }

    this.sessionStartTime.setValue(format(start, 'HH:mm'));
    this.sessionEndTime.setValue(format(end, 'HH:mm'));
    this.synchroniseSessionDuration(false);
  }

  private synchroniseSessionDuration(markDirty = true) {
    const startMinutes = this.timeToMinutes(this.sessionStartTime.value);
    const endMinutes = this.timeToMinutes(this.sessionEndTime.value);
    this.workForm.controls.workMinutes.setValue(
      startMinutes === null || endMinutes === null ? null : endMinutes - startMinutes,
      { emitEvent: false }
    );
    if (markDirty) {
      this.workForm.controls.workMinutes.markAsDirty();
      this.timeEntryError = null;
    }
  }

  private buildSessionTimeEntry(): {
    sessionDate: IsoDate;
    nextEntry: TimeEntry;
  } | null {
    const sessionEntryId = this.data?.sessionEntryId;
    const sessionEntry = sessionEntryId
      ? this.data?.work?.data.timeEntries?.[sessionEntryId]
      : null;
    const originalStart = sessionEntry ? parseISO(sessionEntry.startTime) : null;
    const startMinutes = this.timeToMinutes(this.sessionStartTime.value);
    const endMinutes = this.timeToMinutes(this.sessionEndTime.value);
    if (
      !originalStart ||
      !isValid(originalStart) ||
      startMinutes === null ||
      endMinutes === null ||
      endMinutes <= startMinutes
    ) {
      return null;
    }

    const totalMinutes = endMinutes - startMinutes;
    if (totalMinutes < 1) {
      return null;
    }

    const sessionDate = formatISO(originalStart, { representation: 'date' }) as IsoDate;
    return {
      sessionDate,
      nextEntry: {
        startTime: buildTimeEntryStartTime(sessionDate, startMinutes),
        totalMinutes,
      },
    };
  }

  private timeToMinutes(value: string): number | null {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
    if (!match) {
      return null;
    }

    return Number(match[1]) * 60 + Number(match[2]);
  }

  private getInitialWorkMinutes(work: WorkEntity2 | null): number | null {
    const sessionEntryId = this.data?.sessionEntryId;
    const sessionEntry = sessionEntryId ? work?.timeEntries?.[sessionEntryId] : null;
    return sessionEntry?.totalMinutes ?? work?.workMinutes ?? this.data?.workMinutes ?? null;
  }

  private buildTimeEntries(workMinutes: number | null, date: IsoDate | null) {
    if (!this.data?.startTime) {
      return null;
    }

    const startTime = parseISO(this.data.startTime);
    const entryDate = date ? parseISO(date) : null;
    if (
      !date ||
      !isValid(startTime) ||
      !entryDate ||
      !isValid(entryDate) ||
      !workMinutes ||
      workMinutes <= 0
    ) {
      return null;
    }

    const entryStartTime = buildTimeEntryStartTime(
      date,
      startTime.getHours() * 60 + startTime.getMinutes()
    );

    return appendTimeEntry(null, crypto.randomUUID(), {
      startTime: entryStartTime,
      totalMinutes: workMinutes,
    });
  }

  private async getCustomer(projectId: string) {
    const project = await firstValueFrom(
      this.projects.pipe(map((projects) => projects.find((project) => projectId === project.id)))
    );
    return project?.data.customerId
      ? { id: project.data.customerId, displayName: project.data.customerName as string }
      : null;
  }

  async deleteWork() {
    if (!this.data?.work) {
      return;
    }
    if (this.data.sessionEntryId) {
      const result = await this.workService.deleteWorkSession(
        this.data.work,
        this.data.sessionEntryId
      );
      if (result === 'deleted') {
        this.closeSlideOver();
        return;
      }
      this.closeSlideOver({ saved: true });
      return;
    }

    await this.workService.deleteWork(this.data.work);
    this.closeSlideOver();
  }

  closeSlideOver(result?: { saved: true }) {
    this.dialogRef.close(result);
  }
}
